import { useMutation, useQuery, useQueryClient } from 'react-query';
import instance from './app-index';
import { notification } from 'antd';
import instanceImage from './app-image';

// Insurance Company APIs
const getAllInsurancePatientCompany = async (patientId) => await instance.get(`/insuranceCompanyPatient/patient/${patientId}`);

export function getPatientInsurances(patientId) {
    return instance.get(`/insuranceCompanyPatient/patient/${patientId}`);
}


const getAllPatients = async () => await instance.get('/patient');

export const getPatientByBranch = async (branchId) => await instance.get(`/patient/branch/${branchId}`);

const getPatientByVisit = async (branchId) => await instance.get(`/patient/visit/${branchId}`);


const getPatientByBranchAndVisitStatusOrSearch = async (branchId, search = "", page = 0) => {
    const params = new URLSearchParams();
  
    if (search) {
      params.append("search", search);
    }
  
    params.append("page", page);
  
    const url = `/patient/visits/branch/${branchId}?${params.toString()}`;
  
    return await instance.get(url);
  };
  
  
const getPatientByVisitId = async (visitId) => await instance.get(`/patient/visitid/${visitId}`);

const getPatientVitalsByVisit = async (visitId) => await instance.get(`/vital/visit/${visitId}`);

const searchpatient = async (payload) => await instance.post(`/patient/search`, payload);

const addPatientInsuranceCompany = async (payload) => await instance.post(`/insuranceCompanyPatient`, payload);

const editPatientInsuranceCompany = async (payload) => await instance.put(`/insuranceCompanyPatient/${payload.insuranceCompanyPatientId}`, payload);

const getAllEthnicity = async () => await instance.get(`/ethnicity`);

const addPatient = async (payload) => await instance.post('/patient', payload);

const migratePatientVisit = async ({ payload, branchId }) => await instance.post(`/patientVisit/migrate/${branchId}`, payload);

const addBulkPatient = async (payload) => await instance.post('/patient/bulk', payload);

const editPatient = async ({ patientId, payload }) => await instance.put(`/patient/${patientId}`, payload);

const addVitals = async (payload) => await instance.post('/vital', payload);

const editVitals = async ({payload, vitalId}) => await instance.put(`/vital/${vitalId}`, payload);

const editVisit = async ({payload, visitId}) => await instance.put(`/patientVisit/${visitId}`, payload);

const getPatientVitals = async (patientId) => await instance.get(`/vital/patient/${patientId}`);

const getVisitVitals = async (patientId, visitId) => await instance.get(`/vital/patient/${patientId}/${visitId}`);

const getVitalsByVisit = async (visitId) => await instance.get(`/vital/visit/${visitId}`);

//get vitals by id normal method
export function getVitalsByVisitNormal(visitId) {
    return instance.get(`/vital/visit/${visitId}`);
}

function isVitalRow(row) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return false;
    return row.vitalId != null
        || row.temperatureValue != null
        || row.pulseValue != null
        || row.bpSystolicValue != null
        || row.bpDiastolicValue != null
        || row.respirationValue != null
        || row.oxygenSaturationInput != null
        || row.weightValue != null
        || row.heightValue != null;
}

export function unwrapVitals(response) {
    const body = response?.data ?? response;
    const list = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
            ? body.data
            : Array.isArray(body?.content)
                ? body.content
                : Array.isArray(body?.vitals)
                    ? body.vitals
                    : isVitalRow(body)
                        ? [body]
                        : [];
    return list.filter(isVitalRow);
}

export function pickLatestVital(...responses) {
    const map = new Map();
    responses.forEach((response) => {
        unwrapVitals(response).forEach((row, index) => {
            const key = row.vitalId != null ? `id:${row.vitalId}` : `row:${row.createdAt || ''}:${index}`;
            map.set(key, row);
        });
    });
    const records = [...map.values()];
    if (!records.length) return null;
    return records.sort((a, b) => {
        const stamp = (row) => Math.max(
            Date.parse(row.updatedAt || 0) || 0,
            Date.parse(row.createdAt || 0) || 0
        );
        const diff = stamp(b) - stamp(a);
        if (diff) return diff;
        return (Number(b.vitalId) || 0) - (Number(a.vitalId) || 0);
    })[0];
}

//Patient Issue APIs
const getPatientAllergies = async (patientId, issueType) =>
    await instance.get(`/patientIssue/patient/${patientId}/${issueType}`);

const addPatientAllergy = async (payload) => await instance.post('/patientIssue', payload);

const editPatientAllergy = async ({ patientIssueId, payload }) =>
    await instance.put(`/patientIssue/${patientIssueId}`, payload);

export function unwrapPatientIssues(response) {
    const body = response?.data ?? response;
    const list = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
            ? body.data
            : [];
    return list.filter((row) => row && typeof row === 'object');
}

export function parseAllergyNames(text) {
    const seen = new Set();
    return String(text || '')
        .split(',')
        .map((name) => name.trim())
        .filter((name) => {
            if (!name) return false;
            const key = name.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
}

function planAllergySync(names, existing = []) {
    const originals = (existing || []).filter((row) => row?.patientIssueId);
    const used = new Set();
    const creates = [];
    const updates = [];
    names.forEach((name, index) => {
        const byIndex = originals[index];
        if (byIndex && !used.has(byIndex.patientIssueId)) {
            used.add(byIndex.patientIssueId);
            if ((byIndex.issue || '').trim().toLowerCase() !== name.toLowerCase()) {
                updates.push({ existing: byIndex, name });
            }
            return;
        }
        const match = originals.find((row) => (
            !used.has(row.patientIssueId)
            && (row.issue || '').trim().toLowerCase() === name.toLowerCase()
        ));
        if (match) {
            used.add(match.patientIssueId);
            return;
        }
        creates.push(name);
    });
    return { creates, updates };
}

function allergyIssueDefaults() {
    return {
        issueType: 'ALLERGY',
        comments: '',
        coding: '',
        classificationtype: 'NA',
        occurrence: 'NA',
        severity: '',
        reaction: '',
        verificationStatus: 'UNCONFIRMED',
        referredBy: '',
        outcome: 'UNASSIGNED',
        destination: '',
        status: 'ACTIVE',
    };
}

export function buildAllergyCreatePayload({ name, patientId, visitId, username }) {
    const now = new Date().toISOString();
    return {
        ...allergyIssueDefaults(),
        patientIssueId: 0,
        patientId,
        visitId: visitId || 0,
        issue: name,
        beginDate: now,
        endDate: null,
        createdBy: username,
        createdAt: now,
        updatedBy: '',
        updatedAt: null,
    };
}

export function buildAllergyUpdatePayload({ existing, name, username }) {
    return {
        ...existing,
        ...allergyIssueDefaults(),
        patientIssueId: existing.patientIssueId,
        patientId: existing.patientId,
        visitId: existing.visitId || 0,
        issue: name,
        beginDate: existing.beginDate || new Date().toISOString(),
        endDate: existing.endDate || null,
        createdBy: existing.createdBy,
        createdAt: existing.createdAt,
        updatedBy: username,
        updatedAt: new Date().toISOString(),
    };
}

export async function syncPatientAllergyNames({ names, existing, patientId, visitId, username }) {
    const { creates, updates } = planAllergySync(names, existing);
    for (const name of creates) {
        await addPatientAllergy(buildAllergyCreatePayload({ name, patientId, visitId, username }));
    }
    for (const item of updates) {
        await editPatientAllergy({
            patientIssueId: item.existing.patientIssueId,
            payload: buildAllergyUpdatePayload({ existing: item.existing, name: item.name, username }),
        });
    }
    return { created: creates.length, updated: updates.length };
}

//Patient Consent APIs
const getConsentByVisit = async (visitId) =>
    await instance.get(`/patientConsent/visit/${visitId}`);

const getConsentByPatient = async (patientId) =>
    await instance.get(`/patientConsent/patient/${patientId}`);

const addPatientConsent = async (payload) => await instance.post('/patientConsent', payload);

const editPatientConsent = async ({ patientConsentId, payload }) =>
    await instance.put(`/patientConsent/${patientConsentId}`, payload);

//Patient Vitals Queries
export function useAddVitals() {
    const queryClient = useQueryClient();

    return useMutation(addVitals, { onSuccess: () => {
        queryClient.invalidateQueries('getPatientVitals', { refetchInactive: true });
        queryClient.invalidateQueries('getVisitVitals', { refetchInactive: true });
        queryClient.invalidateQueries('getVitalsByVisit', { refetchInactive: true });
        queryClient.invalidateQueries('getPatientVitalsByVisit', { refetchInactive: true });
    }});
}

export function useEditVitals() {
    const queryClient = useQueryClient(); 

    return useMutation(editVitals, {onSuccess:()=>{
        queryClient.invalidateQueries('getPatientVitals', { refetchInactive: true });
        queryClient.invalidateQueries('getVisitVitals', { refetchInactive: true });
        queryClient.invalidateQueries('getVitalsByVisit', { refetchInactive: true });
        queryClient.invalidateQueries('getPatientVitalsByVisit', { refetchInactive: true });
    }});
}

export function useEditVisit() {
    const queryClient = useQueryClient(); 

    return useMutation(editVisit, {onSuccess:()=>{
        queryClient.invalidateQueries('getVisitById');
    }});
}

export function useGetPatientVitals(patientId) {
    return useQuery(
        ['getPatientVitals', patientId],
        () => getPatientVitals(patientId),
        { enabled: !!patientId }
    );
}

export function useGetVisitVitals(patientId, visitId) {
    return useQuery(
        ['getVisitVitals', patientId, visitId],
        () => getVisitVitals(patientId, visitId),
        { enabled: !!patientId && !!visitId, staleTime: 0, refetchOnMount: 'always' }
    );
}


export function useGetVitalsByVisit(visitId) {
    return useQuery(
        ['getVitalsByVisit', visitId],
        () => getVitalsByVisit(visitId),
        { enabled: !!visitId, staleTime: 0, refetchOnMount: 'always' }
    );
}

// Patient Queries
export function useGetAllPatientInsuranceCompanies(patientId) {
    return useQuery(['getAllInsurancePatientCompany'], () => getAllInsurancePatientCompany(patientId));
}

export function useGetAllPatients() {
    return useQuery(['getAllPatients'], getAllPatients);
}

export function useGetPatientByBranch(branchId) {
    return useQuery(['getPatientByBranch'], () => getPatientByBranch(branchId));
}

export function useGetPatientByVisit(branchId) {
    return useQuery(['getPatientByVisit', branchId],
        () => getPatientByVisit(branchId), { enabled: !!branchId });
}



export function useGetPatientByBranchAndVisitStatusORSearch(branchId, search = "", page = 0) {
    return useQuery(
      ['getPatientByBranchAndVisitStatusOrSearch', branchId, search, page],
      () => getPatientByBranchAndVisitStatusOrSearch(branchId, search, page),
      {
        enabled: !!branchId
      }
    );
}
  
  

export function useGetPatientByVisitId(visitId) {
    return useQuery(['getPatientByVisitId', visitId],
        () => getPatientByVisitId(visitId), { enabled: !!visitId });
}


export function useGetPatientVitalsByVisit(vitalId) {
    return useQuery(['getPatientVitalsByVisit'], () => getPatientVitalsByVisit (vitalId));
}

export function useSearchPatient() {
    return useMutation(searchpatient);
}

export function useAddBulkPatient() {
    const queryClient = useQueryClient();    

    return useMutation(addBulkPatient, {onSuccess:()=>{
        notification.success({ message: "Patients added successfully" });
        queryClient.invalidateQueries('getPatientByVisitId');
    }});
}

export function useAddPatientInsuranceCompany() {
    const queryClient = useQueryClient();    

    return useMutation(addPatientInsuranceCompany, {onSuccess:()=>{
        notification.success({ message: "Insurance company added successfully" });
        queryClient.invalidateQueries('getAllInsurancePatientCompany');
    }});
}

export function useMigratePatientVisit() {
    return useMutation(migratePatientVisit);
}

export function useEditPatientInsuranceCompany() {
    return useMutation(editPatientInsuranceCompany);
}

export function useGetAllEthnicity() {
    return useQuery(['getAllEthnicity'], getAllEthnicity);
}

export function useAddPatient() {
    return useMutation(addPatient);
}

export function useEditPatient() {
    return useMutation(editPatient);
}

//send patient to BC if he/she has not been registered there
const sendPatientToMBC = async (patientId) => await instance.get(`/patient/sendToBC/${patientId}`);

export function useSendPatientToMBC(patientId) {
    return useQuery(['sendPatientToBC', patientId], () => sendPatientToMBC(patientId),{
        enabled: !!patientId,
      });
}


export function getPatients() {
    return instance.get('/patient');
}

export function sendPatientToBC(patientId) {
    return instance.get(`/patient/sendToBC/${patientId}`);
}

export function sendPendingPatientsToBC() {
    return instance.get('/patient/sendPendingPatientToBC');
}

export function getPatientById(patientId) {
    return instance.get(`/patient/${patientId}`);
}

export function getPatientRoaming(patientCardNumber) {
    return instance.get(`/patient/search/card/${patientCardNumber}`);
}

export function getPatientByPatientId(patientId) {
    return instance.get(`/patient/patientId/${patientId}`);
}

export function getEthnicity() {
    return instance.get('/ethnicity');
}

//generate card number for patients
export function generateCardNumberForPatients() {
    return instance.get('/patient/generate-card');
}

export function getInsurancePatientCompany({
    patientId = "",
}) {
    return instance.get(`/insuranceCompanyPatient/patient/${patientId}`);
}

export function insuranceCompanyPatient({
    insuranceCompanyHospitalId = '',
    patientId = '',
    status = '',
    type = '',
    planName = '',
    policyNumber = '',
    subscriberFName = '',
    subscriberMName = '',
    subscriberLName = '',
    relationship = '',
    subscriberEmployer = '',
    copay = '',
    copayType = '',
    policyType = '',
    startDate = '',
    expiryDate = '',
    meansOfVerificationType = '',
    createdBy = ''
} = {}) {
    return instance.post('/insuranceCompanyPatient', {
        insuranceCompanyHospitalId: insuranceCompanyHospitalId,
        patientId: patientId,
        status: status,
        type: type,
        planName: planName,
        policyNumber: policyNumber,
        subscriberFName: subscriberFName,
        subscriberMName: subscriberMName,
        subscriberLName: subscriberLName,
        relationship: relationship,
        subscriberEmployer: subscriberEmployer,
        copay: copay,
        copayType: copayType,
        policyType: policyType,
        startDate: startDate,
        expiryDate: expiryDate,
        meansOfVerificationType: meansOfVerificationType,
        createdBy: createdBy
    });
}

export function getVisitByPatientId(patientId) {
    return instance.get(`/patientVisit/patient/${patientId}`);
}

export function addPatientVisit({
    patientId = '',
    branchId = '',
    visitCategory = '',
    visitDate = '',
    visitTypeId = '',
    visitDescription = '',
    referredHospital = '',
    referredProvider = '',
    preferredProvider = '',
    sensibility = '',
    status = '',
    createdBy = ''

} = {}) {
    if (patientId === '' || branchId === '' || visitCategory === '' || visitDate === '' ||
        visitDescription === '' || preferredProvider === '' || sensibility === ''
    ) {
        return Promise.reject(new Error('Field cannot be empty'));
    }
    return instance.post('/visit', {
        patientId: patientId,
        branchId: branchId,
        visitCategory: visitCategory,
        visitDate: visitDate,
        visitTypeId: visitTypeId,
        visitDescription: visitDescription,
        referredHospital: referredHospital,
        referredProvider: referredProvider,
        preferredProvider: preferredProvider,
        sensibility: sensibility,
        status: status,
        createdBy: createdBy
    });
}

//Patient Visit API
const getPatientVitById = async (patientId) => await instance.get(`/patientVisit/patient/${patientId}`);


const getVisitById = async (patientVisitId) => await instance.get(`/patientVisit/${patientVisitId}`)

// Patient Queries
export function useGetPatientVisitId(patientId) {
    return useQuery(['getPatientVisit', patientId], () => getPatientVitById(patientId), { enabled: !!patientId });
}

export function useGetVisitById(patientVisitId) {
    return useQuery(['getVisitById', patientVisitId], () => getVisitById(patientVisitId), { enabled: !!patientVisitId });
}

const getAllPatientByBranch = async (branchId) => await instance.get(`/patient/branch/${branchId}`);

export function useGetAllPatientByBranch(branchId) {
    return useQuery(['getAllPatientByBranch'], () => getAllPatientByBranch(branchId));
}

const getPatientByBranchId = async (branchId) => await instance.get(`/patient/visit/${branchId}`);

export function useGetPatientByBranchId(branchId) {
    return useQuery(['getPatientByBranchId'], () => getPatientByBranchId(branchId));
}

// Patient Visit API
const getPatientDocument = async (patientId) => await instance.get(`/media/patient/${patientId}`);
const uploadPatientDocument = async (payload) => await instance.post('/media', payload);
const uploadPatientFileName = async ({ filename, payload }) => await instanceImage.post(`/media/upload/${filename}`, payload);

//Patient History API
const getPatientHistory = async (patientId) => await instance.get(`/historyDetail/patient/${patientId}`);

export function useGetPatientHistory(patientId) {
    return useQuery(['getPatientDocument', patientId], () => getPatientHistory(patientId), { enabled: !!patientId });
}

export function useGetPatientDocument(patientId) {
    return useQuery(['getPatientDocument'], () => getPatientDocument(patientId));
}

export function useUploadPatientDocument() {
    return useMutation(uploadPatientDocument, {
        onSuccess: () => {
            notification.success({ message: 'Document uploaded successfully' });
        },
        onError: () => {
            notification.error({ message: 'Document upload failed' });
        }
    });
}

export function useUploadPatientFileName() {
    return useMutation(uploadPatientFileName, {
        onSuccess: () => {
            notification.success({ message: 'Document uploaded successfully' });
        },
        onError: () => {
            notification.error({ message: 'Document upload failed' });
        }
    });
}

//Patient Issues Queries
export function useGetPatientAllergies(patientId, issueType) {
    return useQuery(
        ['getPatientAllergies', patientId, issueType],
        () => getPatientAllergies(patientId, issueType),
        { enabled: !!patientId && !!issueType }
    );
}

export function useAddPatientAllergy() {
    const queryClient = useQueryClient();
    return useMutation(addPatientAllergy,{
        onSuccess: () => {
          queryClient.invalidateQueries(['getPatientAllergies']);
        },
      });
}

export function useEditPatientAllergy() {
    const queryClient = useQueryClient();
    return useMutation(editPatientAllergy, {
        onSuccess: () => {
            queryClient.invalidateQueries(['getPatientAllergies']);
        },
    });
}

//Patient Consent Queries
export function useGetConsentByVisit(visitId) {
    return useQuery(['getConsentByVisit', visitId], () => getConsentByVisit(visitId), { enabled: !!visitId });
}

export function useGetConsentByPatient(patientId) {
    return useQuery(['getConsentByPatient'], () => getConsentByPatient(patientId));
}

export function useAddPatientConsent() {
    const queryClient = useQueryClient();

    return useMutation(addPatientConsent, {
        onSuccess: () => {
            queryClient.invalidateQueries('getConsentByVisit');
            queryClient.invalidateQueries('getConsentByPatient');
        },
    });
}

export function useEditPatientConsent() {
    const queryClient = useQueryClient();

    return useMutation(editPatientConsent, {
        onSuccess: () => {
            queryClient.invalidateQueries('getConsentByVisit');
            queryClient.invalidateQueries('getConsentByPatient');
        },
    });
}
